import * as React from "react"

import { anchorFor, backdropStyle, navigationLinks, renderSection } from "./sections"
import { HONEST, honestBackdrop, isShown } from "./honest"
import "./runtime.css"

/* ============================================================
 *  THE EXPORTED TEMPLATE   (WTM-022, FCS-124)
 * ============================================================
 *  A template composed in the Template Maker, rendered from the
 *  host's view model.
 *
 *  The design is DATA. `document.json` holds the sections and the
 *  order they were put in; this walks it. That is what makes the
 *  export generic: a new design is a new document, not new code,
 *  and every section works because there is one renderer per
 *  section shape rather than one per design.
 *
 *  THE WORDS ARE THE CONTRACTOR'S. The design brings the look; for
 *  the shapes in honest.js the page says only what the contractor
 *  told us, hides a section it has nothing for, and links only to
 *  sections that are on the page. (Before FCS-124 every other word
 *  stayed as authored, which published one design's address,
 *  reviews and claims under a stranger's name.)
 *
 *  COLOUR COMES FROM THE HOST. The builder's brand colours were
 *  design-time. A live site should wear the palette the
 *  contractor chose, and Free Contractor Services already hands
 *  every template exactly that token set — so it is spread here
 *  rather than baked in.
 *
 *  THE LINKS ARE REAL. Each section carries the anchor its pool
 *  answers to, and the nav is built from the sections that are
 *  actually on the page, so a link cannot point at one that is
 *  not there.
 *
 *  `quoteForm` is the host's own lead form (a component taking
 *  `view`), so the hero's form is a real one that submits, never a
 *  picture of one. Without it the hero simply has no form.
 * ============================================================ */

/** The two tokens the host does not already provide. */
function fill(style) {
  return {
    ...style,
    "--hero-overlay-centred":
      style["--hero-overlay-centred"] ||
      `linear-gradient(180deg, ${style["--overlay-ink"] || "rgba(10,15,20,.45)"}66, ${
        style["--overlay-ink"] || "rgba(10,15,20,.84)"
      }d6)`,
    "--photo-fill":
      style["--photo-fill"] ||
      `linear-gradient(145deg, ${style["--surface-3"] || "#dfe5e1"}, ${style["--ink-3"] || "#55637a"})`,
  }
}

export function TemplateFromDocument({ document: doc, view, quoteForm }) {
  // The sections that have something to say. The nav is made from these, never from the whole document.
  const sections = React.useMemo(() => ((doc && doc.sections) || []).filter((section) => isShown(section, view)), [doc, view])
  const nav = React.useMemo(() => navigationLinks(sections), [sections])

  // One anchor per pool, on the first section of it — which is what the nav
  // points at. Later sections of the same pool do not fight it for the id.
  const claimed = new Set()

  return (
    // The frame measures, the page scales. Two elements because the page is
    // what gets scaled, and an element cannot ask how wide it is and then
    // change how wide it is.
    <div className="wtm-frame">
    <div
      className={`wtm-site wtm-${(doc && doc.design) || "editorial"}`}
      data-polish={(doc && doc.polish) || "crisp"}
      data-type={(doc && doc.typography) || "grotesque"}
      style={fill((view && view.style) || {})}
    >
      {sections.map((section, index) => {
        const anchor = anchorFor(section.category)
        const id = anchor && !claimed.has(anchor) ? anchor : undefined
        if (id) claimed.add(anchor)
        const honest = HONEST[section.variant]

        return (
          <section
            key={section.nodeId || index}
            id={id}
            className={`tm-preview-section tm-section-${section.variant}`}
            style={honest ? honestBackdrop(section.variant, view) : backdropStyle(section, view)}
          >
            {honest ? honest.render({ view, section, nav, QuoteForm: quoteForm, position: index + 1 }) : renderSection(section, view, index + 1, nav)}
          </section>
        )
      })}
    </div>
    </div>
  )
}

export default TemplateFromDocument
